import React, { useState, useCallback, useMemo } from 'react';
import {
  Upload, Loader2, FileSpreadsheet, Download, CheckCircle2, AlertTriangle,
  Truck, Sparkles, Package, ClipboardList,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';

// Excel-driven pick-slip creation: upload → preview (validate + auto-recommend
// transport, overridable per slip) → confirm to create all valid slips.
export const ExcelPickSlipUpload = ({ transporters = [], onCreated }) => {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [transportBySlip, setTransportBySlip] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [created, setCreated] = useState(null);

  const reset = () => {
    setPreview(null); setTransportBySlip({}); setFile(null);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f); setPreview(null); setTransportBySlip({}); setCreated(null);
  };

  const downloadTemplate = useCallback(() => {
    const rows = [
      ['Halnode Pick Slip No', 'DC Number', 'Box'],
      ['HAL-0001', 'DC-10001-26-27', 'SP-DIRECT-01-BOX1'],
      ['HAL-0001', 'DC-10001-26-27', 'DIRECT-STK-2'],
      ['HAL-0002', 'DC-10002-26-27', 'SP-DIRECT-02-BOX1'],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'seasonal_pick_slip_template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const doPreview = async () => {
    if (!file) { toast.error('Choose an Excel or CSV file first'); return; }
    setPreviewing(true); setPreview(null); setCreated(null);
    try {
      const res = await api.previewPickSlipExcel(file);
      setPreview(res);
      // Pre-fill transport from each valid slip's recommendation.
      const init = {};
      (res.slips || []).forEach((s, i) => {
        if (s.valid && s.recommendation?.recommended_transport_id) {
          init[i] = s.recommendation.recommended_transport_id;
        }
      });
      setTransportBySlip(init);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read the file');
    } finally { setPreviewing(false); }
  };

  const validSlips = useMemo(
    () => (preview?.slips || []).map((s, i) => ({ ...s, _idx: i })).filter((s) => s.valid),
    [preview],
  );

  const readyCount = useMemo(
    () => validSlips.filter((s) => transportBySlip[s._idx]).length,
    [validSlips, transportBySlip],
  );

  const doConfirm = async () => {
    const missing = validSlips.filter((s) => !transportBySlip[s._idx]);
    if (missing.length) { toast.error('Select a transport for every slip before creating'); return; }
    if (!validSlips.length) { toast.error('No valid slips to create'); return; }
    const payload = {
      slips: validSlips.map((s) => {
        const tid = transportBySlip[s._idx];
        const t = transporters.find((x) => x.transport_id === tid);
        return {
          halnode_pick_slip_no: s.halnode_pick_slip_no,
          dc_number: s.dc_number,
          customer: s.customer,
          place: s.place || '',
          box_ids: s.box_ids,
          transport_id: tid,
          transport_name: t?.name || '',
        };
      }),
    };
    setConfirming(true);
    try {
      const res = await api.confirmPickSlipExcel(payload);
      toast.success(res.message);
      setCreated(res);
      reset();
      onCreated?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && Array.isArray(d.problems)) {
        toast.error(`${d.problems.length} problem(s) — see below`);
        setPreview((p) => (p ? { ...p, confirm_problems: d.problems } : p));
      } else {
        toast.error(d || 'Failed to create pick slips');
      }
    } finally { setConfirming(false); }
  };

  return (
    <div className="space-y-5" data-testid="excel-upload-tab">
      {created && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="excel-created-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-800">{created.message}</p>
            <p className="text-emerald-700">
              {created.assigned_count} auto-assigned to a Seasonal picker
              {created.assigned_count < created.created_count ? ' · set a Seasonal picker for the rest in Available Pickers' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Upload card */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1F2E]">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Upload a box list (.xlsx or .csv)
          </div>
          <p className="text-xs text-[#8A8F9E]">
            Columns needed: <b>Halnode Pick Slip No</b>, <b>DC Number</b>, <b>Box</b> (system Box Number OR
            small-box sticker code). Rows are grouped into one pick slip per Halnode + DC. Customer is auto-derived
            and transport is auto-recommended (you can override each below).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".xlsx,.xlsm,.csv"
              onChange={onFile}
              className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-indigo-600 file:text-white file:px-3 file:py-1.5 file:text-sm file:cursor-pointer"
              data-testid="excel-file-input"
            />
            <Button onClick={doPreview} disabled={!file || previewing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="excel-preview-btn">
              {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Preview
            </Button>
            <Button variant="outline" onClick={downloadTemplate} data-testid="excel-template-btn">
              <Download className="w-4 h-4 mr-2" /> Download template
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <>
          {/* Summary */}
          <div className="flex flex-wrap items-center gap-3" data-testid="excel-summary">
            <Badge className="bg-emerald-100 text-emerald-700 border-0">{preview.summary.valid_slips} valid slip(s)</Badge>
            <Badge className="bg-sky-100 text-sky-700 border-0">{preview.summary.total_boxes} box(es)</Badge>
            <Badge className="bg-amber-100 text-amber-700 border-0">{preview.summary.total_qty} pcs</Badge>
            {preview.summary.invalid_slips > 0 && (
              <Badge className="bg-rose-100 text-rose-700 border-0">{preview.summary.invalid_slips} slip(s) with issues</Badge>
            )}
            <div className="flex-1" />
            <Button
              onClick={doConfirm}
              disabled={confirming || readyCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="excel-confirm-btn"
            >
              {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-2" />}
              Create {readyCount} Pick Slip{readyCount === 1 ? '' : 's'}
            </Button>
          </div>

          {/* File-level parse errors */}
          {(preview.file_errors?.length > 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1" data-testid="excel-file-errors">
              <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Some rows were skipped:</p>
              {preview.file_errors.map((m, i) => <p key={i}>• {m}</p>)}
            </div>
          )}

          {/* Confirm-time problems (if a race caused a re-validation failure) */}
          {(preview.confirm_problems?.length > 0) && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 space-y-1" data-testid="excel-confirm-problems">
              <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Nothing was created — fix these and re-preview:</p>
              {preview.confirm_problems.map((m, i) => <p key={i}>• {m}</p>)}
            </div>
          )}

          {/* Per-slip cards */}
          <div className="space-y-4">
            {(preview.slips || []).map((s, i) => (
              <Card key={i} className={s.valid ? '' : 'border-rose-200 bg-rose-50/40'} data-testid={`excel-slip-${i}`}>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-semibold text-sm text-[#1A1F2E]" data-testid={`excel-slip-hal-${i}`}>{s.halnode_pick_slip_no}</span>
                    <Badge className="bg-gray-100 text-gray-700 border-0 text-[11px]">DC {s.dc_number}</Badge>
                    {s.customer && <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[11px]">{s.customer}{s.place ? ` — ${s.place}` : ''}</Badge>}
                    <Badge className="bg-sky-100 text-sky-700 border-0 text-[11px]">{s.box_count} box · {s.total_qty} pcs</Badge>
                    {s.valid
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px]">Ready</Badge>
                      : <Badge className="bg-rose-100 text-rose-700 border-0 text-[11px]">Has issues</Badge>}
                  </div>

                  {s.errors?.length > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-md p-2 text-xs text-rose-800 space-y-1" data-testid={`excel-slip-errors-${i}`}>
                      {s.errors.map((m, j) => <p key={j}>• {m}</p>)}
                    </div>
                  )}

                  {/* Boxes */}
                  {s.boxes?.length > 0 && (
                    <div className="rounded-md border border-gray-100 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-[#8A8F9E] bg-gray-50">
                          <th className="py-1.5 px-2">Box No</th><th>Small-box</th><th>Slug</th><th>Bin</th><th className="text-right px-2">Qty</th>
                        </tr></thead>
                        <tbody>
                          {s.boxes.map((b) => (
                            <tr key={b.box_id} className="border-t">
                              <td className="py-1.5 px-2 font-mono">{b.box_number}</td>
                              <td className="font-mono text-[#5A5F6E]">{b.external_box_no || '—'}</td>
                              <td>{b.slug || '—'}</td>
                              <td className="text-[#5A5F6E]">{b.footprint || 'no bin'}</td>
                              <td className="text-right px-2 font-semibold">{b.total_qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Transport (only for valid slips) */}
                  {s.valid && (
                    <div>
                      <label className="text-xs text-[#5A5F6E] flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Transport *</label>
                      {s.recommendation?.recommended_transport_name && (
                        <p className="text-xs text-[#2D5F4F] flex items-center gap-1.5 my-1" data-testid={`excel-slip-reco-${i}`}>
                          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                          Recommended: <b>{s.recommendation.recommended_transport_name}</b>
                          {s.recommendation.total_weight_kg != null ? <span className="text-[#8A8F9E]"> · {s.recommendation.total_weight_kg} kg</span> : null}
                        </p>
                      )}
                      <Select value={transportBySlip[i] || ''} onValueChange={(v) => setTransportBySlip((p) => ({ ...p, [i]: v }))}>
                        <SelectTrigger data-testid={`excel-slip-transport-${i}`}>
                          <SelectValue placeholder={transporters.length ? 'Select transport' : 'No transporters found'} />
                        </SelectTrigger>
                        <SelectContent>
                          {transporters.map((t) => {
                            const isReco = t.transport_id === s.recommendation?.recommended_transport_id;
                            return (
                              <SelectItem key={t.transport_id} value={t.transport_id} data-testid={`excel-slip-${i}-transport-${t.transport_id}`}>
                                {t.name}{isReco ? '  ★ recommended' : ''}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {!preview && !created && (
        <div className="text-center text-[#8A8F9E] py-8 text-sm flex flex-col items-center gap-2">
          <Package className="w-8 h-8 opacity-40" />
          Upload a file and hit Preview to see the pick slips it will create.
        </div>
      )}
    </div>
  );
};

export default ExcelPickSlipUpload;
